/* eslint-disable max-lines */
import { type models } from "jiffy-api";
import { useCallback } from "react";

import { useProductColors } from "@design-lab/entities/product";
import { PRODUCT_ID, STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import {
  trackDesignUploadCompleted,
  trackDesignUploadFailed,
  trackDesignUploadStarted,
  trackImageDeleted
} from "@design-lab/shared/lib/analytics";
import { useExperiments } from "@design-lab/shared/lib/config";
import { getFileUrl, isPreviewBasedFile } from "@design-lab/shared/lib/file";
import { calculateImageDpi } from "@design-lab/shared/lib/imageUtils";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import {
  useDftUploadArtwork,
  useDtfSideTracking
} from "@design-lab/widgets/design-lab-upload-artwork";
import api from "common/utils/api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import {
  clearArtworkFormValues,
  setArtworkDimensions,
  setArtworkId
} from "../lib/artworkFormHelpers";
import type { UploadArtworkParams } from "./types";
import { useArtwork } from "./useArtwork";
import { useArtworkForm } from "./useArtworkForm";
import { useImagesWarmFastCache } from "./useImagesFastCache";

export function useArtworkImage() {
  const channel = useDtfChannel();
  const { markSideAsActive, markSideAsDeleted, isSideActive } = useDtfSideTracking();
  const { form } = useArtworkForm();
  const { composition } = useArtboard();
  const { warmCache } = useImagesWarmFastCache();
  const { mutateAsync: uploadArtworkMutation } = useDftUploadArtwork();
  const { front, back, getMachineBySide } = useArtwork();
  const { currentProduct } = useProductColors();
  const experimentsConfig = useExperiments();

  const getBusinessLineAndOrderType = useCallback(() => {
    const catalogNumber = currentProduct?.catalogNumber || STANDARD_DTF_CATALOG_NUMBER;

    // Design lab is always used for print-on-demand services
    const businessLine = "Print";
    const orderType = "print";

    return { businessLine, orderType, catalogNumber };
  }, [currentProduct]);

  const uploadArtwork = async ({
    file,
    side,
    source,
    existingArtworkId,
    sourceComposition = composition,
    aiImageVersionId,
    communityImageId,
    experiments = JSON.stringify(experimentsConfig),
    uploadSource = "design-lab"
  }: UploadArtworkParams) => {
    const uploadStartTime = performance.now();

    const { businessLine, orderType, catalogNumber } = getBusinessLineAndOrderType();

    const surface = sourceComposition?.surfaces?.[side];
    const dpi = await calculateImageDpi(file, surface);

    const uploadUuid = crypto.randomUUID();

    trackDesignUploadStarted(side, source || "upload", {
      file,
      catalogNumber,
      businessLine,
      orderType,
      page: "design_lab",
      pdpType: orderType,
      projectId: null,
      multiArtwork: false,
      artworksCount: 1,
      dpi,
      preCutTransfers: false,
      uploadStartTime,
      uploadUuid
    });

    const dtfChannelUuid = await channel.retrieveDtfChannelUuid();
    const machine = side === "front" ? front : back;

    // If machine is in finished state, reset it to initial for new upload
    // This happens during auto-upload when the machine was restored with existing artwork
    if (machine.state.matches("finished")) {
      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          visitedStates: [],
          cacheWarmingStatus: "none",
          feedbackSelectedReason: undefined,
          shouldShowFeedback: false
        }
      });
    }

    if (!dtfChannelUuid) {
      const errorMessage = "DTF channel UUID is not available";

      machine.send({
        type: "reset",
        context: {
          artworkId: existingArtworkId ? parseInt(existingArtworkId, 10) : undefined,
          visitedStates: [],
          cacheWarmingStatus: "none",
          feedbackSelectedReason: undefined
        }
      });

      throw new Error(errorMessage);
    }

    const url = isPreviewBasedFile(file) ? await getFileUrl(file) : undefined;
    const [cachedImage] = await warmCache([url]);

    setArtworkDimensions({
      form,
      side,
      cachedImage,
      surfaces: sourceComposition?.surfaces
    });

    const processTimeStamp = Date.now();

    markSideAsActive(side, String(processTimeStamp));

    const fields = {
      file,
      productId: PRODUCT_ID,
      fileOriginalIndex: "0",
      dtfChannelUuid,
      preCut: "false",
      processTimeStamp: String(processTimeStamp),
      existingArtworkId: existingArtworkId || "",
      aiImageVersionId: aiImageVersionId || "",
      communityImageId: communityImageId || "",
      uploadSource,
      experiments
    } as models.dtf.uploadArtwork.Request;

    machine.send({
      type: "upload.started",
      file,
      source: source || "upload",
      processTimeStamp: String(processTimeStamp)
    });

    let response;
    try {
      response = objNamesToCamelCase(await uploadArtworkMutation(fields));
    } catch (error) {
      const errorData = (error as any)?.response?.data;
      const isValidationError =
        errorData?.error_type === "backend_file_validation" || errorData?.validation_error;
      const errorMessage = errorData?.validation_error || errorData?.error || "Unknown error";

      trackDesignUploadFailed({
        type: isValidationError ? "validation_error" : "unhandled_error",
        errorMessage
      });

      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          artworkId: existingArtworkId ? parseInt(existingArtworkId, 10) : undefined,
          visitedStates: [],
          cacheWarmingStatus: "none",
          feedbackSelectedReason: undefined,
          shouldShowFeedback: false
        }
      });
      throw error;
    }

    machine.send({
      type: "upload.completed",
      id: Number(response.id)
    });

    machine.send({
      type: "set.originalImageUrl",
      originalImageUrl: response.originalImageUrl
    });

    if (!isSideActive(side, String(processTimeStamp))) {
      return;
    }

    const {
      businessLine: completionBusinessLine,
      orderType: completionOrderType,
      catalogNumber: completionCatalogNumber
    } = getBusinessLineAndOrderType();

    const completionSurface = sourceComposition?.surfaces?.[side];
    const completionDpi = await calculateImageDpi(file, completionSurface);
    const id = response.id.toString();

    trackDesignUploadCompleted(side, id, source || "upload", {
      file,
      catalogNumber: completionCatalogNumber,
      businessLine: completionBusinessLine,
      orderType: completionOrderType,
      page: "design_lab",
      pdpType: completionOrderType,
      projectId: null,
      multiArtwork: false,
      artworksCount: 1,
      dpi: completionDpi,
      preCutTransfers: false,
      uploadStartTime,
      uploadUuid
    });

    setArtworkId(form, side, id);
  };

  const cancelArtworkProcess = useCallback(async (artworkId: number) => {
    try {
      await api.dtf.cancelArtworkProcess({ id: artworkId }, { catalog_number: "POD001" });
    } catch (error) {
      window.Honeybadger?.notify("cancelArtworkProcess failed", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          artworkId,
          location: "design-lab"
        }
      });
    }
  }, []);

  const deleteArtwork = useCallback(
    async (placement: string, artworkId?: string) => {
      const side = placement === "back" ? "back" : "front";
      const machine = getMachineBySide(side);

      // If enhancement is in progress and we have an artworkId, cancel the process
      const isEnhancementInProgress = machine.state.hasTag("loading");
      if (isEnhancementInProgress && artworkId) {
        cancelArtworkProcess(Number(artworkId));
      }

      markSideAsDeleted(side);
      machine.send({ type: "reset" });
      trackImageDeleted(side, artworkId);
      clearArtworkFormValues(form, placement);
    },
    [form, getMachineBySide, markSideAsDeleted, cancelArtworkProcess]
  );

  return {
    uploadArtwork,
    deleteArtwork,
    markSideAsDeleted
  };
}
